/**
 * Transcript Editor page for NaraHub.
 * Provides rich editing for documents with observations format.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDocument, useDocumentView } from "../hooks/useDocuments";
import { useProject } from "../hooks/useProjects";
import { useWorkspaceUrl } from "../lib/routes";
import { PageHeader } from "../components/layout/PageHeader";
import { KiiEditor, type NMPhoto } from "../components/kii/kii-editor";
import { DebugProvider } from "../contexts/DebugContext";
import { updateDocumentContent, updateDocumentName, rotatePhoto, createDocumentVersion, deleteFile } from "../services/naraDoor";
import { useFileUpload } from "../hooks/useFileUpload";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { ArrowLeft, Mic } from "lucide-react";
import type { TranscriptJSON } from "../shared/schema";

export function TranscriptEditor() {
  const { t } = useTranslation();
  const { projectId, id: documentId } = useParams<{
    projectId: string;
    id: string;
  }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const urls = useWorkspaceUrl();
  const { activeWorkspace } = useWorkspace();

  // Hooks now get workspaceId automatically from the store
  const { data: document, isLoading: documentLoading, isFetching: documentFetching } = useDocument(projectId, documentId);
  const { data: documentView, isLoading: viewLoading, isFetching: viewFetching } = useDocumentView(projectId, documentId, document?.status);
  const { data: project } = useProject(projectId);

  const [transcriptData, setTranscriptData] = useState<TranscriptJSON | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  // A failed autosave or rename stays on screen until the next save succeeds.
  const [saveFailed, setSaveFailed] = useState(false);
  const [photoDeleteFailed, setPhotoDeleteFailed] = useState(false);
  // A failed rotation stays on screen (once, though it retries) until a rotation succeeds.
  const [photoRotateFailed, setPhotoRotateFailed] = useState(false);
  const [documentName, setDocumentName] = useState<string>("");
  const [isSavingName, setIsSavingName] = useState(false);
  const [pendingRotations, setPendingRotations] = useState<Record<string, number>>({});
  const [processingPhotos, setProcessingPhotos] = useState<string[]>([]);
  const rotationsRef = useRef<Record<string, number>>({});
  const sentAnglesRef = useRef<Record<string, number>>({});
  const processingRef = useRef<Set<string>>(new Set());
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // Undo stack state
  const undoStackRef = useRef<TranscriptJSON[]>([]);
  const isUndoingRef = useRef(false);
  const hasSavedRef = useRef(false);
  const previousTranscriptRef = useRef<TranscriptJSON | null>(null);
  const [canUndo, setCanUndo] = useState(false);

  // Photo upload hook for adding photos to existing document
  const {
    uploadToDocument,
    isUploading: isUploadingPhotos,
    fileProgress: uploadProgress,
    error: uploadError,
  } = useFileUpload({
    projectId: projectId || "",
    onComplete: () => {
      // Invalidate document view to refresh image_files
      if (documentId) {
        queryClient.invalidateQueries({ queryKey: ["documentView", documentId] });
      }
    },
  });

  // Drafts start from server data that is not being refetched, never from a stale cached copy.
  // Once started, a draft is never overwritten.
  useEffect(() => {
    if (document?.name && !documentName && !documentFetching) {
      setDocumentName(document.name);
    }
  }, [document, documentFetching]);

  // Initialize transcript data from document
  useEffect(() => {
    if (documentView?.content_json && !transcriptData && !viewFetching) {
      const content = documentView.content_json as any;

      // Validate it has observations format
      if (content.observations && Array.isArray(content.observations)) {
        // Check for valid format
        const firstObs = content.observations[0];
        if (firstObs &&
            typeof firstObs.description === 'string' &&
            typeof firstObs.start_time === 'number' &&
            typeof firstObs.end_time === 'number' &&
            Array.isArray(firstObs.photo_ids)) {
          // Ensure we have segments array (required by schema)
          const data: TranscriptJSON = {
            segments: content.segments || [],
            observations: content.observations,
            language: content.language,
          };
          setTranscriptData(data);
          previousTranscriptRef.current = structuredClone(data);
        }
      }
    }
  }, [documentView, transcriptData, viewFetching]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: TranscriptJSON) => {
      if (!projectId || !documentId) throw new Error("Document ID required");
      setIsSaving(true);
      return updateDocumentContent(projectId, documentId, data);
    },
    onSuccess: () => {
      setHasChanges(false);
      setIsSaving(false);
      setSaveFailed(false);
      setLastSaved(new Date());
      queryClient.invalidateQueries({ queryKey: ["document", documentId] });
      queryClient.invalidateQueries({ queryKey: ["documentView", documentId] });
    },
    onError: (error: any) => {
      setIsSaving(false);
      console.error("Failed to save transcript:", error);
      setSaveFailed(true);
    },
  });

  // Autosave effect
  useEffect(() => {
    if (!hasChanges || !transcriptData) return;

    const timeoutId = setTimeout(() => {
      // Push the pre-change state onto undo stack (unless we're undoing)
      if (!isUndoingRef.current && previousTranscriptRef.current) {
        undoStackRef.current.push(structuredClone(previousTranscriptRef.current));
        if (undoStackRef.current.length > 50) {
          undoStackRef.current.shift();
        }
        setCanUndo(true);
      }
      isUndoingRef.current = false;
      hasSavedRef.current = true;
      previousTranscriptRef.current = structuredClone(transcriptData);
      saveMutation.mutate(transcriptData);
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [transcriptData, hasChanges]);

  // Save document name mutation
  const saveNameMutation = useMutation({
    mutationFn: async (name: string) => {
      if (!projectId || !documentId) throw new Error("Document ID required");
      setIsSavingName(true);
      return updateDocumentName(projectId, documentId, name);
    },
    onSuccess: () => {
      setIsSavingName(false);
      setSaveFailed(false);
      queryClient.invalidateQueries({ queryKey: ["document", documentId] });
      queryClient.invalidateQueries({ queryKey: ["documents"] });
    },
    onError: (error: any) => {
      setIsSavingName(false);
      console.error("Failed to save document name:", error);
      setSaveFailed(true);
      // Revert to original name
      if (document?.name) {
        setDocumentName(document.name);
      }
    },
  });

  const handleTranscriptChange = (newData: TranscriptJSON) => {
    setTranscriptData(newData);
    setHasChanges(true);
  };

  // Undo handler: pop the last saved state from the stack
  const handleUndo = useCallback(() => {
    const stack = undoStackRef.current;
    if (stack.length === 0) return;
    const previous = stack.pop()!;
    isUndoingRef.current = true;
    setTranscriptData(previous);
    setHasChanges(true);
    setCanUndo(stack.length > 0);
  }, []);

  // Keyboard shortcut for undo (Ctrl+Z / Cmd+Z)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo]);

  const handleNameSave = () => {
    const trimmedName = documentName.trim();
    if (!trimmedName || trimmedName === document?.name) {
      // Revert if empty or unchanged
      if (document?.name) {
        setDocumentName(document.name);
      }
      return;
    }
    saveNameMutation.mutate(trimmedName);
  };

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimers.current).forEach(clearTimeout);
    };
  }, []);

  const fireRotation = (photoId: string) => {
    const angle = rotationsRef.current[photoId];
    if (!angle || !documentId || !projectId || !activeWorkspace?.id) return;

    sentAnglesRef.current[photoId] = angle;
    processingRef.current.add(photoId);
    setProcessingPhotos([...processingRef.current]);

    rotatePhoto({
      fileId: photoId,
      workspaceId: activeWorkspace.id,
      projectId: projectId,
      documentId: documentId,
      angle: angle as 90 | 180 | 270,
    }).then(() => {
      setPhotoRotateFailed(false);
      // Only invalidate signed URLs — document structure hasn't changed
      queryClient.invalidateQueries({ queryKey: ["/api/nm-files/signed-url"] });
    }).catch((error) => {
      console.error("Failed to rotate photo:", error);
      setPhotoRotateFailed(true);
      // Server didn't apply rotation — keep full accumulated rotation and retry
      delete sentAnglesRef.current[photoId];
      processingRef.current.delete(photoId);
      setProcessingPhotos([...processingRef.current]);

      const remaining = rotationsRef.current[photoId];
      if (remaining && remaining > 0) {
        debounceTimers.current[photoId] = setTimeout(() => {
          delete debounceTimers.current[photoId];
          fireRotation(photoId);
        }, 2000);
      }
    });
  };

  const handleRotatePhoto = (photoId: string, angle: 90 | 180 | 270) => {
    if (!documentId || !projectId || !activeWorkspace?.id) return;

    const current = rotationsRef.current[photoId] ?? 0;
    const next = (current + angle) % 360;

    if (next === 0 && !processingRef.current.has(photoId)) {
      // Full circle with no backend call in flight — cancel everything
      delete rotationsRef.current[photoId];
      setPendingRotations(prev => {
        const { [photoId]: _, ...rest } = prev;
        return rest;
      });
      if (debounceTimers.current[photoId]) {
        clearTimeout(debounceTimers.current[photoId]);
        delete debounceTimers.current[photoId];
      }
      return;
    }

    rotationsRef.current[photoId] = next;
    setPendingRotations(prev => ({ ...prev, [photoId]: next }));

    // Only start debounce if not currently processing — new debounce fires from handleClearPendingRotation
    if (!processingRef.current.has(photoId)) {
      if (debounceTimers.current[photoId]) {
        clearTimeout(debounceTimers.current[photoId]);
      }
      debounceTimers.current[photoId] = setTimeout(() => {
        delete debounceTimers.current[photoId];
        fireRotation(photoId);
      }, 2000);
    }
  };

  const handleClearPendingRotation = useCallback((photoId: string) => {
    const sent = sentAnglesRef.current[photoId] ?? 0;
    const current = rotationsRef.current[photoId] ?? 0;
    const remaining = (current - sent + 360) % 360;

    delete sentAnglesRef.current[photoId];
    processingRef.current.delete(photoId);
    setProcessingPhotos([...processingRef.current]);

    if (remaining > 0) {
      rotationsRef.current[photoId] = remaining;
      setPendingRotations(prev => ({ ...prev, [photoId]: remaining }));
      // Start a new debounce for the remaining rotation
      debounceTimers.current[photoId] = setTimeout(() => {
        delete debounceTimers.current[photoId];
        fireRotation(photoId);
      }, 2000);
    } else {
      delete rotationsRef.current[photoId];
      setPendingRotations(prev => {
        const { [photoId]: _, ...rest } = prev;
        return rest;
      });
    }
  }, []);

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleNameSave();
      (e.target as HTMLInputElement).blur();
    } else if (e.key === 'Escape') {
      if (document?.name) {
        setDocumentName(document.name);
      }
      (e.target as HTMLInputElement).blur();
    }
  };

  // Photo management handlers
  const handlePermanentDeletePhoto = useCallback(async (photoId: string) => {
    if (!projectId || !documentId) return;
    try {
      await deleteFile(projectId, documentId, photoId);
      setPhotoDeleteFailed(false);
      queryClient.invalidateQueries({ queryKey: ["documentView", documentId] });
    } catch (error) {
      console.error("Failed to delete file:", error);
      setPhotoDeleteFailed(true);
    }
  }, [projectId, documentId, queryClient]);

  const handleUploadPhotos = useCallback(async (files: File[]) => {
    if (!documentId) return;
    await uploadToDocument(files, documentId);
  }, [documentId, uploadToDocument]);

  const handleBack = useCallback(async () => {
    if (hasSavedRef.current && projectId && documentId) {
      try {
        await createDocumentVersion(projectId, documentId, "Edit session");
      } catch (error) {
        console.error("Failed to create version on exit:", error);
      }
    }
    if (urls && projectId && documentId) {
      navigate(urls.document(projectId, documentId));
    }
  }, [documentId, projectId, urls, navigate]);

  // Best-effort version creation on tab/window close
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (hasSavedRef.current && projectId && documentId) {
        // The page is closing: no one is left to read a message, so the failure is only logged.
        createDocumentVersion(projectId, documentId, "Edit session").catch((error) => {
          console.error("Failed to create version on close:", error);
        });
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [projectId, documentId]);

  // Loading state
  if (documentLoading || viewLoading) {
    return (
      <div className="space-y-4">
        <div className="h-10 w-48 bg-muted rounded animate-pulse" />
        <div className="h-64 bg-muted rounded animate-pulse" />
        <div className="h-64 bg-muted rounded animate-pulse" />
      </div>
    );
  }

  // No document found
  if (!document || !documentView) {
    return (
      <div className="bg-card rounded-lg border border-border p-8 text-center">
        <h2 className="text-lg font-medium text-foreground mb-2">
          {t("transcriptEditor.notFound")}
        </h2>
        <p className="text-muted-foreground mb-4">
          {t("transcriptEditor.notFoundDescription")}
        </p>
        {urls && (
          <Link
            to={projectId ? urls.project(projectId) : urls.projects}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t("transcriptEditor.backToProject")}
          </Link>
        )}
      </div>
    );
  }

  // No transcript data or invalid format
  if (!transcriptData) {
    return (
      <div className="bg-card rounded-lg border border-border p-8 text-center">
        <h2 className="text-lg font-medium text-foreground mb-2">
          {t("transcriptEditor.noTranscript")}
        </h2>
        <p className="text-muted-foreground mb-4">
          {t("transcriptEditor.noTranscriptDescription")}
        </p>
        <button
          onClick={handleBack}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("transcriptEditor.backToDocument")}
        </button>
      </div>
    );
  }

  // Get audio and image files from documentView
  const audioFile = (documentView as any)?.audio_file;
  const imageFiles: NMPhoto[] = (documentView as any)?.image_files || [];
  const hasAudio = audioFile?.url;

  return (
    <div className="space-y-4">
      {/* Mobile back button */}
      <div className="sm:hidden">
        <button
          onClick={handleBack}
          className="inline-flex items-center gap-1 px-2 py-1.5 text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="text-sm">{t("common.back")}</span>
        </button>
      </div>

      <PageHeader
        title={t("transcriptEditor.title")}
        breadcrumbs={[
          { label: t("nav.projects"), href: urls?.projects },
          {
            label: project?.name || t("transcriptEditor.project"),
            href: urls && projectId ? urls.project(projectId) : undefined,
          },
          {
            label: document.name,
            href: urls && projectId && documentId ? urls.document(projectId, documentId) : undefined,
          },
          { label: t("transcriptEditor.title") },
        ]}
        actions={
          <button
            onClick={handleBack}
            className="hidden sm:inline-flex items-center gap-2 px-3 py-2 text-foreground border border-border rounded-lg hover:bg-accent transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("transcriptEditor.backToDocument")}
          </button>
        }
      />

      {/* Document name editing */}
      <div className="bg-card rounded-lg border border-border p-3 sm:p-4">
        <label className="text-xs sm:text-sm font-medium text-muted-foreground mb-1.5 sm:mb-2 block">
          {t("transcriptEditor.documentName")}
        </label>
        <div className="flex gap-2 items-center">
          <input
            type="text"
            value={documentName}
            onChange={(e) => setDocumentName(e.target.value)}
            onBlur={handleNameSave}
            onKeyDown={handleNameKeyDown}
            className="flex-1 text-base sm:text-lg font-semibold px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={t("transcriptEditor.enterDocumentName")}
            disabled={isSavingName}
          />
          {isSavingName && (
            <span className="text-xs sm:text-sm text-muted-foreground">{t("transcriptEditor.saving")}</span>
          )}
        </div>
      </div>

      {/* Audio player */}
      {hasAudio && (
        <div className="mb-6 bg-card rounded-lg border border-border p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <Mic className="h-4 w-4" />
            <span className="truncate">{audioFile.filename}</span>
            {audioFile.size && (
              <span className="text-xs">
                ({(audioFile.size / 1024 / 1024).toFixed(2)} MB)
              </span>
            )}
          </div>
          <audio controls className="w-full" preload="metadata">
            <source src={audioFile.url} type={audioFile.mime_type || 'audio/mpeg'} />
            {t("transcriptEditor.audioNotSupported")}
          </audio>
        </div>
      )}

      {saveFailed && (
        <p role="alert" className="mb-4 text-sm text-destructive">
          {t("documentDetail.saveError")}
        </p>
      )}

      {(uploadError || photoDeleteFailed) && (
        <p role="alert" className="mb-4 text-sm text-destructive">
          {t(uploadError ? "documentDetail.photoUploadError" : "documentDetail.photoDeleteError")}
        </p>
      )}

      {photoRotateFailed && (
        <p role="alert" className="mb-4 text-sm text-destructive">
          {t("documentDetail.photoRotateError")}
        </p>
      )}

      {/* KiiEditor */}
      <div className="bg-card rounded-lg border border-border p-6">
        <DebugProvider>
          <KiiEditor
            transcriptData={transcriptData}
            onTranscriptChange={handleTranscriptChange}
            attachments={[]}
            nmPhotos={imageFiles}
            isNMDocument={true}
            isSaving={isSaving}
            lastSaved={lastSaved}
            hasChanges={hasChanges}
            onRotatePhoto={handleRotatePhoto}
            pendingRotations={pendingRotations}
            onClearPendingRotation={handleClearPendingRotation}
            processingPhotoIds={processingPhotos}
            onUndo={handleUndo}
            canUndo={canUndo}
            onPermanentDeletePhoto={handlePermanentDeletePhoto}
            onUploadPhotos={handleUploadPhotos}
            uploadProgress={uploadProgress}
            isUploadingPhotos={isUploadingPhotos}
          />
        </DebugProvider>
      </div>
    </div>
  );
}
